import type { Locale } from "./locales.js";
import type { Row } from "./messages/types.js";
import { chatMessages } from "./messages/chat.js";
import { characterMessages } from "./messages/character.js";
import { gameMessages } from "./messages/game.js";
import { gameEditorMessages } from "./messages/game-editor.js";
import { hubMessages } from "./messages/hub.js";
import { lorebookMessages } from "./messages/lorebook.js";
import { scriptMessages } from "./messages/script.js";
import { settingsExtraMessages } from "./messages/settings-extra.js";
import { miscMessages } from "./messages/misc.js";

/**
 * UI 번역 문자열 — **키별(key-major)** 구조. 한 키의 모든 언어 번역을 한곳에 모아
 * 점진 번역·검수가 쉽도록 한다. 누락 로케일은 translate가 en-US → ko 순으로 폴백한다.
 *
 * 번역 원칙: 기계 번역이 아니라 지역 늬앙스를 반영한다(예: 설정 = 스페인 Ajustes /
 * 포르투갈 Definições / 브라질 Configurações). en-GB는 의미 차이가 없으면 생략(en-US
 * 폴백)하고, 영국/미국 차이가 있는 키만 채운다.
 *
 * 구조: 아래 BASE_MESSAGES(내비/공통/정규식/프리셋/페르소나/프로바이더/기본 설정)에
 * 더해 영역별 모듈(messages/*.ts)을 머지한다. 새 화면을 번역할 때는 해당 영역 모듈에
 * 키를 추가하면 되며, TKey 유니온은 머지 결과에서 자동 파생된다.
 */

/** 키별 번역. en-US는 항상 채운다(폴백 기준). */
const BASE_MESSAGES = {
  "nav.chats": {
    ko: "대화", "en-US": "Chats", ja: "チャット", zh: "对话", fr: "Discussions",
    de: "Chats", es: "Chats", it: "Chat", pt: "Conversas", "pt-BR": "Conversas",
    ru: "Чаты", ar: "المحادثات", hi: "चैट", id: "Obrolan",
  },
  "nav.characters": {
    ko: "캐릭터", "en-US": "Characters", ja: "キャラクター", zh: "角色", fr: "Personnages",
    de: "Charaktere", es: "Personajes", it: "Personaggi", pt: "Personagens", "pt-BR": "Personagens",
    ru: "Персонажи", ar: "الشخصيات", hi: "पात्र", id: "Karakter",
  },
  "nav.games": {
    ko: "게임", "en-US": "Games", ja: "ゲーム", zh: "游戏", fr: "Jeux",
    de: "Spiele", es: "Juegos", it: "Giochi", pt: "Jogos", "pt-BR": "Jogos",
    ru: "Игры", ar: "الألعاب", hi: "खेल", id: "Game",
  },
  "nav.gallery": {
    ko: "갤러리", "en-US": "Gallery", ja: "ギャラリー", zh: "图库", fr: "Galerie",
    de: "Galerie", es: "Galería", it: "Galleria", pt: "Galeria", "pt-BR": "Galeria",
    ru: "Галерея", ar: "المعرض", hi: "गैलरी", id: "Galeri",
  },
  "nav.lorebooks": {
    ko: "로어북", "en-US": "Lorebooks", ja: "ロアブック", zh: "设定集", fr: "Codex",
    de: "Lorebooks", es: "Compendios", it: "Codex", pt: "Compêndios", "pt-BR": "Compêndios",
    ru: "Лорбуки", ar: "كتب المعرفة", hi: "लोरबुक", id: "Lorebook",
  },
  "nav.hub": {
    ko: "허브", "en-US": "Hub", ja: "Hub", zh: "Hub", fr: "Hub",
    de: "Hub", es: "Hub", it: "Hub", pt: "Hub", "pt-BR": "Hub",
    ru: "Hub", ar: "Hub", hi: "Hub", id: "Hub",
  },
  "nav.guide": {
    ko: "가이드", "en-US": "Guide", ja: "ガイド", zh: "指南", fr: "Guide",
    de: "Anleitung", es: "Guía", it: "Guida", pt: "Guia", "pt-BR": "Guia",
    ru: "Руководство", ar: "الدليل", hi: "गाइड", id: "Panduan",
  },
  "nav.settings": {
    ko: "설정", "en-US": "Settings", ja: "設定", zh: "设置", fr: "Paramètres",
    de: "Einstellungen", es: "Ajustes", it: "Impostazioni", pt: "Definições", "pt-BR": "Configurações",
    ru: "Настройки", ar: "الإعدادات", hi: "सेटिंग्स", id: "Pengaturan",
  },
  "app.tagline": {
    ko: "로컬 우선 · 오프라인 동작", "en-US": "Local-first · Works offline",
    ja: "ローカル優先 · オフライン対応", zh: "本地优先 · 离线可用",
    fr: "Local d'abord · Fonctionne hors ligne", de: "Lokal zuerst · Offline nutzbar",
    es: "Local primero · Funciona sin conexión", it: "Locale prima · Funziona offline",
    pt: "Local primeiro · Funciona offline", "pt-BR": "Local primeiro · Funciona offline",
    ru: "Локально · Работает офлайн", ar: "محلي أولاً · يعمل دون اتصال",
    hi: "लोकल-फर्स्ट · ऑफ़लाइन काम करता है", id: "Lokal dulu · Berfungsi luring",
  },
  "common.loading": {
    ko: "불러오는 중...", "en-US": "Loading...", ja: "読み込み中...", zh: "加载中...",
    fr: "Chargement...", de: "Wird geladen...", es: "Cargando...", it: "Caricamento...",
    pt: "A carregar...", "pt-BR": "Carregando...", ru: "Загрузка...", ar: "جارٍ التحميل...",
    hi: "लोड हो रहा है...", id: "Memuat...",
  },
  "common.save": {
    ko: "저장", "en-US": "Save", ja: "保存", zh: "保存", fr: "Enregistrer",
    de: "Speichern", es: "Guardar", it: "Salva", pt: "Guardar", "pt-BR": "Salvar",
    ru: "Сохранить", ar: "حفظ", hi: "सहेजें", id: "Simpan",
  },
  "common.saving": {
    ko: "저장 중...", "en-US": "Saving...", ja: "保存中...", zh: "保存中...", fr: "Enregistrement...",
    de: "Wird gespeichert...", es: "Guardando...", it: "Salvataggio...", pt: "A guardar...", "pt-BR": "Salvando...",
    ru: "Сохранение...", ar: "جارٍ الحفظ...", hi: "सहेजा जा रहा है...", id: "Menyimpan...",
  },
  "common.cancel": {
    ko: "취소", "en-US": "Cancel", ja: "キャンセル", zh: "取消", fr: "Annuler",
    de: "Abbrechen", es: "Cancelar", it: "Annulla", pt: "Cancelar", "pt-BR": "Cancelar",
    ru: "Отмена", ar: "إلغاء", hi: "रद्द करें", id: "Batal",
  },
  "common.apply": {
    ko: "적용", "en-US": "Apply", ja: "適用", zh: "应用", fr: "Appliquer",
    de: "Anwenden", es: "Aplicar", it: "Applica", pt: "Aplicar", "pt-BR": "Aplicar",
    ru: "Применить", ar: "تطبيق", hi: "लागू करें", id: "Terapkan",
  },
  "common.delete": {
    ko: "삭제", "en-US": "Delete", ja: "削除", zh: "删除", fr: "Supprimer",
    de: "Löschen", es: "Eliminar", it: "Elimina", pt: "Eliminar", "pt-BR": "Excluir",
    ru: "Удалить", ar: "حذف", hi: "हटाएँ", id: "Hapus",
  },
  "common.default": {
    ko: "기본", "en-US": "Default", ja: "デフォルト", zh: "默认", fr: "Par défaut",
    de: "Standard", es: "Predeterminado", it: "Predefinito", pt: "Predefinido", "pt-BR": "Padrão",
    ru: "По умолчанию", ar: "افتراضي", hi: "डिफ़ॉल्ट", id: "Bawaan",
  },
  "common.makeDefault": {
    ko: "기본으로", "en-US": "Set as default", ja: "デフォルトに設定", zh: "设为默认",
    fr: "Définir par défaut", de: "Als Standard", es: "Establecer por defecto", it: "Imposta come predefinito",
    pt: "Definir como predefinido", "pt-BR": "Definir como padrão", ru: "Сделать основным",
    ar: "تعيين كافتراضي", hi: "डिफ़ॉल्ट बनाएँ", id: "Jadikan bawaan",
  },
  "common.edit": {
    ko: "편집", "en-US": "Edit", ja: "編集", zh: "编辑", fr: "Modifier",
    de: "Bearbeiten", es: "Editar", it: "Modifica", pt: "Editar", "pt-BR": "Editar",
    ru: "Изменить", ar: "تعديل", hi: "संपादित करें", id: "Edit",
  },
  "common.export": {
    ko: "내보내기", "en-US": "Export", ja: "エクスポート", zh: "导出", fr: "Exporter",
    de: "Exportieren", es: "Exportar", it: "Esporta", pt: "Exportar", "pt-BR": "Exportar",
    ru: "Экспорт", ar: "تصدير", hi: "निर्यात", id: "Ekspor",
  },
  "common.import": {
    ko: "가져오기", "en-US": "Import", ja: "インポート", zh: "导入", fr: "Importer",
    de: "Importieren", es: "Importar", it: "Importa", pt: "Importar", "pt-BR": "Importar",
    ru: "Импорт", ar: "استيراد", hi: "आयात", id: "Impor",
  },
  "common.moveUp": {
    ko: "위로", "en-US": "Move up", ja: "上へ", zh: "上移", fr: "Monter",
    de: "Nach oben", es: "Subir", it: "Su", pt: "Mover para cima", "pt-BR": "Mover para cima",
    ru: "Вверх", ar: "أعلى", hi: "ऊपर", id: "Naik",
  },
  "common.moveDown": {
    ko: "아래로", "en-US": "Move down", ja: "下へ", zh: "下移", fr: "Descendre",
    de: "Nach unten", es: "Bajar", it: "Giù", pt: "Mover para baixo", "pt-BR": "Mover para baixo",
    ru: "Вниз", ar: "أسفل", hi: "नीचे", id: "Turun",
  },
  "common.enabled": {
    ko: "활성화", "en-US": "Enabled", ja: "有効", zh: "启用", fr: "Activé",
    de: "Aktiviert", es: "Activado", it: "Attivo", pt: "Ativado", "pt-BR": "Ativado",
    ru: "Включено", ar: "مُفعّل", hi: "सक्षम", id: "Aktif",
  },
  "common.disabledBadge": {
    ko: "비활성", "en-US": "Off", ja: "無効", zh: "停用", fr: "Désactivé",
    de: "Aus", es: "Inactivo", it: "Inattivo", pt: "Inativo", "pt-BR": "Inativo",
    ru: "Выкл.", ar: "معطّل", hi: "बंद", id: "Nonaktif",
  },
  "rx.title": {
    ko: "정규식 스크립트", "en-US": "Regex scripts", ja: "正規表現スクリプト", zh: "正则脚本",
    fr: "Scripts regex", de: "Regex-Skripte", es: "Scripts de regex", it: "Script regex",
    pt: "Scripts regex", "pt-BR": "Scripts de regex", ru: "Regex-скрипты",
    ar: "نصوص التعبيرات النمطية", hi: "रेगेक्स स्क्रिप्ट", id: "Skrip regex",
  },
  "rx.empty": {
    ko: "정규식 스크립트로 메시지 표시를 변형할 수 있어요. 만든 뒤 캐릭터 편집에서 연결하세요.",
    "en-US": "Use regex scripts to transform how messages display. After creating one, link it in the character editor.",
    ja: "正規表現スクリプトでメッセージの表示を変換できます。作成後、キャラクター編集で連携してください。",
    zh: "用正则脚本可改变消息的显示方式。创建后，在角色编辑中关联即可。",
    fr: "Utilisez des scripts regex pour transformer l'affichage des messages. Après en avoir créé un, liez-le dans l'éditeur de personnage.",
    de: "Mit Regex-Skripten kannst du die Anzeige von Nachrichten umwandeln. Nach dem Erstellen im Charakter-Editor verknüpfen.",
    es: "Usa scripts de regex para transformar cómo se muestran los mensajes. Tras crear uno, vincúlalo en el editor de personaje.",
    it: "Usa gli script regex per trasformare la visualizzazione dei messaggi. Dopo averne creato uno, collegalo nell'editor del personaggio.",
    pt: "Usa scripts regex para transformar a apresentação das mensagens. Depois de criar um, associa-o no editor de personagem.",
    "pt-BR": "Use scripts de regex para transformar como as mensagens são exibidas. Após criar um, vincule-o no editor de personagem.",
    ru: "Regex-скрипты меняют отображение сообщений. После создания привяжите скрипт в редакторе персонажа.",
    ar: "استخدم نصوص التعبيرات النمطية لتحويل طريقة عرض الرسائل. بعد إنشاء نص، اربطه في محرّر الشخصية.",
    hi: "रेगेक्स स्क्रिप्ट से संदेशों का प्रदर्शन बदलें। बनाने के बाद पात्र संपादक में जोड़ें।",
    id: "Gunakan skrip regex untuk mengubah tampilan pesan. Setelah membuatnya, tautkan di editor karakter.",
  },
  "rx.add": {
    ko: "+ 정규식 스크립트 추가", "en-US": "+ Add regex script", ja: "+ 正規表現スクリプトを追加",
    zh: "+ 添加正则脚本", fr: "+ Ajouter un script regex", de: "+ Regex-Skript hinzufügen",
    es: "+ Añadir script de regex", it: "+ Aggiungi script regex", pt: "+ Adicionar script regex",
    "pt-BR": "+ Adicionar script de regex", ru: "+ Добавить regex-скрипт",
    ar: "+ إضافة نص تعبير نمطي", hi: "+ रेगेक्स स्क्रिप्ट जोड़ें", id: "+ Tambah skrip regex",
  },
  "rx.deleteConfirm": {
    ko: "\"{name}\" 스크립트를 삭제할까요?", "en-US": "Delete script \"{name}\"?",
    ja: "スクリプト「{name}」を削除しますか？", zh: "删除脚本“{name}”？",
    fr: "Supprimer le script « {name} » ?", de: "Skript „{name}“ löschen?",
    es: "¿Eliminar el script «{name}»?", it: "Eliminare lo script «{name}»?",
    pt: "Eliminar o script \"{name}\"?", "pt-BR": "Excluir o script \"{name}\"?",
    ru: "Удалить скрипт «{name}»?", ar: "حذف النص \"{name}\"؟",
    hi: "स्क्रिप्ट \"{name}\" हटाएँ?", id: "Hapus skrip \"{name}\"?",
  },
  "rx.namePlaceholder": {
    ko: "스크립트 이름", "en-US": "Script name", ja: "スクリプト名", zh: "脚本名称",
    fr: "Nom du script", de: "Skriptname", es: "Nombre del script", it: "Nome dello script",
    pt: "Nome do script", "pt-BR": "Nome do script", ru: "Название скрипта",
    ar: "اسم النص", hi: "स्क्रिप्ट नाम", id: "Nama skrip",
  },
  "rx.patternPlaceholder": {
    ko: "정규식 패턴 (예: \\*(.+?)\\*)", "en-US": "Regex pattern (e.g. \\*(.+?)\\*)",
    ja: "正規表現パターン（例: \\*(.+?)\\*）", zh: "正则模式（例如：\\*(.+?)\\*）",
    fr: "Motif regex (ex. : \\*(.+?)\\*)", de: "Regex-Muster (z. B. \\*(.+?)\\*)",
    es: "Patrón de regex (p. ej., \\*(.+?)\\*)", it: "Pattern regex (es. \\*(.+?)\\*)",
    pt: "Padrão regex (ex.: \\*(.+?)\\*)", "pt-BR": "Padrão de regex (ex.: \\*(.+?)\\*)",
    ru: "Regex-шаблон (напр., \\*(.+?)\\*)", ar: "نمط التعبير النمطي (مثال: \\*(.+?)\\*)",
    hi: "रेगेक्स पैटर्न (उदा. \\*(.+?)\\*)", id: "Pola regex (mis. \\*(.+?)\\*)",
  },
  "rx.flagsPlaceholder": {
    ko: "flags", "en-US": "flags", ja: "flags", zh: "标志", fr: "flags",
    de: "Flags", es: "flags", it: "flag", pt: "flags", "pt-BR": "flags",
    ru: "флаги", ar: "أعلام", hi: "flags", id: "flag",
  },
  "rx.flagsTitle": {
    ko: "정규식 플래그 (예: gi)", "en-US": "Regex flags (e.g. gi)", ja: "正規表現フラグ（例: gi）",
    zh: "正则标志（例如：gi）", fr: "Flags regex (ex. : gi)", de: "Regex-Flags (z. B. gi)",
    es: "Flags de regex (p. ej., gi)", it: "Flag regex (es. gi)", pt: "Flags regex (ex.: gi)",
    "pt-BR": "Flags de regex (ex.: gi)", ru: "Regex-флаги (напр., gi)", ar: "أعلام التعبير النمطي (مثال: gi)",
    hi: "रेगेक्स फ़्लैग (उदा. gi)", id: "Flag regex (mis. gi)",
  },
  "rx.replacementPlaceholder": {
    ko: "치환 결과 (예: <em>$1</em>)", "en-US": "Replacement (e.g. <em>$1</em>)",
    ja: "置換結果（例: <em>$1</em>）", zh: "替换结果（例如：<em>$1</em>）",
    fr: "Remplacement (ex. : <em>$1</em>)", de: "Ersetzung (z. B. <em>$1</em>)",
    es: "Reemplazo (p. ej., <em>$1</em>)", it: "Sostituzione (es. <em>$1</em>)",
    pt: "Substituição (ex.: <em>$1</em>)", "pt-BR": "Substituição (ex.: <em>$1</em>)",
    ru: "Замена (напр., <em>$1</em>)", ar: "الاستبدال (مثال: <em>$1</em>)",
    hi: "प्रतिस्थापन (उदा. <em>$1</em>)", id: "Pengganti (mis. <em>$1</em>)",
  },
  "rx.testPlaceholder": {
    ko: "테스트 입력을 쓰면 결과 미리보기", "en-US": "Type test input to preview the result",
    ja: "テスト入力を書くと結果をプレビュー", zh: "输入测试文本以预览结果",
    fr: "Saisissez un texte de test pour prévisualiser le résultat", de: "Testeingabe eingeben, um das Ergebnis zu sehen",
    es: "Escribe una entrada de prueba para previsualizar el resultado", it: "Scrivi un input di prova per l'anteprima",
    pt: "Escreve um texto de teste para pré-visualizar o resultado", "pt-BR": "Digite um texto de teste para visualizar o resultado",
    ru: "Введите тестовый текст для предпросмотра", ar: "اكتب نص اختبار لمعاينة النتيجة",
    hi: "परिणाम पूर्वावलोकन हेतु परीक्षण इनपुट लिखें", id: "Ketik input uji untuk pratinjau hasil",
  },
  "rx.regexError": {
    ko: "(정규식 오류)", "en-US": "(regex error)", ja: "（正規表現エラー）", zh: "（正则错误）",
    fr: "(erreur regex)", de: "(Regex-Fehler)", es: "(error de regex)", it: "(errore regex)",
    pt: "(erro de regex)", "pt-BR": "(erro de regex)", ru: "(ошибка regex)",
    ar: "(خطأ في التعبير النمطي)", hi: "(रेगेक्स त्रुटि)", id: "(galat regex)",
  },
  "rx.errNamePattern": {
    ko: "이름과 패턴은 필수입니다.", "en-US": "Name and pattern are required.",
    ja: "名前とパターンは必須です。", zh: "名称和模式为必填项。",
    fr: "Le nom et le motif sont obligatoires.", de: "Name und Muster sind erforderlich.",
    es: "El nombre y el patrón son obligatorios.", it: "Nome e pattern sono obbligatori.",
    pt: "O nome e o padrão são obrigatórios.", "pt-BR": "Nome e padrão são obrigatórios.",
    ru: "Название и шаблон обязательны.", ar: "الاسم والنمط مطلوبان.",
    hi: "नाम और पैटर्न आवश्यक हैं।", id: "Nama dan pola wajib diisi.",
  },
  "rx.errPlacement": {
    ko: "적용 단계를 하나 이상 선택하세요.", "en-US": "Select at least one stage to apply.",
    ja: "適用する段階を 1 つ以上選択してください。", zh: "请至少选择一个应用阶段。",
    fr: "Sélectionnez au moins une étape d'application.", de: "Wähle mindestens eine Anwendungsstufe.",
    es: "Selecciona al menos una etapa de aplicación.", it: "Seleziona almeno una fase di applicazione.",
    pt: "Seleciona pelo menos uma etapa de aplicação.", "pt-BR": "Selecione pelo menos uma etapa de aplicação.",
    ru: "Выберите хотя бы один этап применения.", ar: "اختر مرحلة تطبيق واحدة على الأقل.",
    hi: "लागू करने हेतु कम से कम एक चरण चुनें।", id: "Pilih minimal satu tahap penerapan.",
  },
  "rx.placeUserInput": {
    ko: "사용자 입력", "en-US": "User input", ja: "ユーザー入力", zh: "用户输入",
    fr: "Saisie utilisateur", de: "Benutzereingabe", es: "Entrada del usuario", it: "Input utente",
    pt: "Entrada do utilizador", "pt-BR": "Entrada do usuário", ru: "Ввод пользователя",
    ar: "إدخال المستخدم", hi: "उपयोगकर्ता इनपुट", id: "Input pengguna",
  },
  "rx.placeAiOutput": {
    ko: "AI 출력", "en-US": "AI output", ja: "AI 出力", zh: "AI 输出", fr: "Sortie de l'IA",
    de: "KI-Ausgabe", es: "Salida de IA", it: "Output IA", pt: "Saída da IA", "pt-BR": "Saída da IA",
    ru: "Вывод ИИ", ar: "مخرجات الذكاء الاصطناعي", hi: "AI आउटपुट", id: "Output AI",
  },
  "rx.placeSlash": {
    ko: "슬래시 명령", "en-US": "Slash command", ja: "スラッシュコマンド", zh: "斜杠命令",
    fr: "Commande slash", de: "Slash-Befehl", es: "Comando de barra", it: "Comando slash",
    pt: "Comando barra", "pt-BR": "Comando de barra", ru: "Слэш-команда",
    ar: "أمر شرطة مائلة", hi: "स्लैश कमांड", id: "Perintah garis miring",
  },
  "rx.placeWorldInfo": {
    ko: "월드 인포", "en-US": "World info", ja: "ワールドインフォ", zh: "世界信息",
    fr: "Infos du monde", de: "Welt-Info", es: "Info del mundo", it: "Info del mondo",
    pt: "Info do mundo", "pt-BR": "Info do mundo", ru: "Мир (world info)",
    ar: "معلومات العالم", hi: "वर्ल्ड इन्फो", id: "Info dunia",
  },
  "rx.placeDisplayOnly": {
    ko: "표시 전용", "en-US": "Display only", ja: "表示のみ", zh: "仅显示",
    fr: "Affichage seulement", de: "Nur Anzeige", es: "Solo visualización", it: "Solo visualizzazione",
    pt: "Apenas apresentação", "pt-BR": "Apenas exibição", ru: "Только отображение",
    ar: "العرض فقط", hi: "केवल प्रदर्शन", id: "Hanya tampilan",
  },
  "preset.title": {
    ko: "프리셋 (프롬프트 & 샘플러)", "en-US": "Presets (prompt & sampler)",
    ja: "プリセット（プロンプト & サンプラー）", zh: "预设（提示词与采样器）",
    fr: "Préréglages (prompt et échantillonneur)", de: "Presets (Prompt & Sampler)",
    es: "Preajustes (prompt y muestreador)", it: "Preset (prompt e sampler)",
    pt: "Predefinições (prompt e amostrador)", "pt-BR": "Predefinições (prompt e amostrador)",
    ru: "Пресеты (промпт и сэмплер)", ar: "الإعدادات المسبقة (الموجّه والمُعاين)",
    hi: "प्रीसेट (प्रॉम्प्ट और सैम्पलर)", id: "Praatur (prompt & sampler)",
  },
  "preset.newName": {
    ko: "새 프리셋", "en-US": "New preset", ja: "新しいプリセット", zh: "新预设",
    fr: "Nouveau préréglage", de: "Neues Preset", es: "Nuevo preajuste", it: "Nuovo preset",
    pt: "Nova predefinição", "pt-BR": "Nova predefinição", ru: "Новый пресет",
    ar: "إعداد مسبق جديد", hi: "नया प्रीसेट", id: "Praatur baru",
  },
  "preset.imported": {
    ko: "{n}개 프리셋 가져옴.", "en-US": "Imported {n} preset(s).", ja: "{n} 件のプリセットを取り込みました。",
    zh: "已导入 {n} 个预设。", fr: "{n} préréglage(s) importé(s).", de: "{n} Preset(s) importiert.",
    es: "{n} preajuste(s) importado(s).", it: "{n} preset importati.", pt: "{n} predefinição(ões) importada(s).",
    "pt-BR": "{n} predefinição(ões) importada(s).", ru: "Импортировано пресетов: {n}.",
    ar: "تم استيراد {n} إعداد مسبق.", hi: "{n} प्रीसेट आयात किए।", id: "{n} praatur diimpor.",
  },
  "preset.deleteConfirm": {
    ko: "\"{name}\" 프리셋을 삭제할까요?", "en-US": "Delete preset \"{name}\"?",
    ja: "プリセット「{name}」を削除しますか？", zh: "删除预设“{name}”？",
    fr: "Supprimer le préréglage « {name} » ?", de: "Preset „{name}“ löschen?",
    es: "¿Eliminar el preajuste «{name}»?", it: "Eliminare il preset «{name}»?",
    pt: "Eliminar a predefinição \"{name}\"?", "pt-BR": "Excluir a predefinição \"{name}\"?",
    ru: "Удалить пресет «{name}»?", ar: "حذف الإعداد المسبق \"{name}\"؟",
    hi: "प्रीसेट \"{name}\" हटाएँ?", id: "Hapus praatur \"{name}\"?",
  },
  "preset.add": {
    ko: "+ 프리셋 추가", "en-US": "+ Add preset", ja: "+ プリセットを追加", zh: "+ 添加预设",
    fr: "+ Ajouter un préréglage", de: "+ Preset hinzufügen", es: "+ Añadir preajuste",
    it: "+ Aggiungi preset", pt: "+ Adicionar predefinição", "pt-BR": "+ Adicionar predefinição",
    ru: "+ Добавить пресет", ar: "+ إضافة إعداد مسبق", hi: "+ प्रीसेट जोड़ें", id: "+ Tambah praatur",
  },
  "preset.backToList": {
    ko: "‹ 목록", "en-US": "‹ List", ja: "‹ 一覧", zh: "‹ 列表", fr: "‹ Liste",
    de: "‹ Liste", es: "‹ Lista", it: "‹ Elenco", pt: "‹ Lista", "pt-BR": "‹ Lista",
    ru: "‹ Список", ar: "‹ القائمة", hi: "‹ सूची", id: "‹ Daftar",
  },
  "preset.sampling": {
    ko: "샘플링", "en-US": "Sampling", ja: "サンプリング", zh: "采样", fr: "Échantillonnage",
    de: "Sampling", es: "Muestreo", it: "Campionamento", pt: "Amostragem", "pt-BR": "Amostragem",
    ru: "Сэмплинг", ar: "المعاينة", hi: "सैम्पलिंग", id: "Sampling",
  },
  "preset.maxTokens": {
    ko: "최대 토큰", "en-US": "Max tokens", ja: "最大トークン", zh: "最大令牌",
    fr: "Tokens max", de: "Max. Tokens", es: "Tokens máx.", it: "Token max",
    pt: "Tokens máx.", "pt-BR": "Tokens máx.", ru: "Макс. токенов", ar: "أقصى عدد للرموز",
    hi: "अधिकतम टोकन", id: "Token maks.",
  },
  "preset.contextSize": {
    ko: "컨텍스트 크기", "en-US": "Context size", ja: "コンテキストサイズ", zh: "上下文大小",
    fr: "Taille du contexte", de: "Kontextgröße", es: "Tamaño del contexto", it: "Dimensione del contesto",
    pt: "Tamanho do contexto", "pt-BR": "Tamanho do contexto", ru: "Размер контекста",
    ar: "حجم السياق", hi: "कॉन्टेक्स्ट आकार", id: "Ukuran konteks",
  },
  "preset.blocksTitle": {
    ko: "프롬프트 블록 (위→아래 순서로 조립 · 손잡이를 길게 눌러 이동)",
    "en-US": "Prompt blocks (assembled top→bottom · long-press the handle to move)",
    ja: "プロンプトブロック（上→下の順に組み立て · ハンドルを長押しで移動）",
    zh: "提示词块（自上而下组装 · 长按手柄移动）",
    fr: "Blocs de prompt (assemblés de haut en bas · appui long sur la poignée pour déplacer)",
    de: "Prompt-Blöcke (von oben nach unten zusammengesetzt · Griff gedrückt halten zum Verschieben)",
    es: "Bloques de prompt (se ensamblan de arriba abajo · mantén pulsado el asa para mover)",
    it: "Blocchi del prompt (assemblati dall'alto in basso · tieni premuta la maniglia per spostare)",
    pt: "Blocos de prompt (montados de cima para baixo · prima sem soltar a pega para mover)",
    "pt-BR": "Blocos de prompt (montados de cima para baixo · pressione e segure a alça para mover)",
    ru: "Блоки промпта (собираются сверху вниз · долгое нажатие на ручку для перемещения)",
    ar: "كتل الموجّه (تُجمَّع من الأعلى إلى الأسفل · اضغط مطولًا على المقبض للتحريك)",
    hi: "प्रॉम्प्ट ब्लॉक (ऊपर→नीचे क्रम में जुड़ते हैं · ले जाने हेतु हैंडल को देर तक दबाएँ)",
    id: "Blok prompt (dirakit atas→bawah · tekan lama gagang untuk memindahkan)",
  },
  "preset.dragHandle": {
    ko: "끌어서 이동", "en-US": "Drag to move", ja: "ドラッグして移動", zh: "拖动以移动",
    fr: "Glisser pour déplacer", de: "Zum Verschieben ziehen", es: "Arrastra para mover",
    it: "Trascina per spostare", pt: "Arrasta para mover", "pt-BR": "Arraste para mover",
    ru: "Перетащите для перемещения", ar: "اسحب للتحريك", hi: "ले जाने हेतु खींचें", id: "Seret untuk memindahkan",
  },
  "preset.dragHandleTitle": {
    ko: "길게 눌러 이동", "en-US": "Long-press to move", ja: "長押しで移動", zh: "长按移动",
    fr: "Appui long pour déplacer", de: "Gedrückt halten zum Verschieben", es: "Mantén pulsado para mover",
    it: "Tieni premuto per spostare", pt: "Prime sem soltar para mover", "pt-BR": "Pressione e segure para mover",
    ru: "Долгое нажатие для перемещения", ar: "اضغط مطولًا للتحريك", hi: "ले जाने हेतु देर तक दबाएँ", id: "Tekan lama untuk memindahkan",
  },
  "preset.toggleUse": {
    ko: "{name} 사용", "en-US": "Use {name}", ja: "{name} を使用", zh: "使用 {name}",
    fr: "Utiliser {name}", de: "{name} verwenden", es: "Usar {name}", it: "Usa {name}",
    pt: "Usar {name}", "pt-BR": "Usar {name}", ru: "Использовать {name}", ar: "استخدام {name}",
    hi: "{name} उपयोग करें", id: "Gunakan {name}",
  },
  "block.system": {
    ko: "시스템 프롬프트", "en-US": "System prompt", ja: "システムプロンプト", zh: "系统提示词",
    fr: "Prompt système", de: "System-Prompt", es: "Prompt del sistema", it: "Prompt di sistema",
    pt: "Prompt do sistema", "pt-BR": "Prompt do sistema", ru: "Системный промпт",
    ar: "موجّه النظام", hi: "सिस्टम प्रॉम्प्ट", id: "Prompt sistem",
  },
  "block.persona": {
    ko: "페르소나", "en-US": "Persona", ja: "ペルソナ", zh: "角色身份", fr: "Persona",
    de: "Persona", es: "Persona", it: "Persona", pt: "Persona", "pt-BR": "Persona",
    ru: "Персона", ar: "الشخصية", hi: "पर्सोना", id: "Persona",
  },
  "persona.noDescription": {
    ko: "(설명 없음)", "en-US": "(No description)", ja: "(説明なし)", zh: "(无描述)",
    fr: "(Pas de description)", de: "(Keine Beschreibung)", es: "(Sin descripción)", it: "(Nessuna descrizione)",
    pt: "(Sem descrição)", "pt-BR": "(Sem descrição)", ru: "(Нет описания)", ar: "(بدون وصف)",
    hi: "(कोई विवरण नहीं)", id: "(Tidak ada deskripsi)",
  },
  "persona.deleted": {
    ko: "페르소나가 삭제되었습니다.", "en-US": "Persona deleted.", ja: "ペルソナが削除されました。", zh: "角色身份已删除。",
    fr: "Persona supprimé.", de: "Persona gelöscht.", es: "Persona eliminada.", it: "Persona eliminata.",
    pt: "Persona eliminada.", "pt-BR": "Persona excluída.", ru: "Персона удалена.", ar: "تم حذف الشخصية.",
    hi: "पर्सोना हटा दिया गया।", id: "Persona dihapus.",
  },
  "block.charDescription": {
    ko: "캐릭터 설명", "en-US": "Character description", ja: "キャラクター説明", zh: "角色描述",
    fr: "Description du personnage", de: "Charakterbeschreibung", es: "Descripción del personaje",
    it: "Descrizione del personaggio", pt: "Descrição da personagem", "pt-BR": "Descrição do personagem",
    ru: "Описание персонажа", ar: "وصف الشخصية", hi: "पात्र विवरण", id: "Deskripsi karakter",
  },
  "block.scenario": {
    ko: "시나리오", "en-US": "Scenario", ja: "シナリオ", zh: "场景", fr: "Scénario",
    de: "Szenario", es: "Escenario", it: "Scenario", pt: "Cenário", "pt-BR": "Cenário",
    ru: "Сценарий", ar: "السيناريو", hi: "परिदृश्य", id: "Skenario",
  },
  "block.exampleDialogue": {
    ko: "대화 예시", "en-US": "Example dialogue", ja: "会話例", zh: "对话示例",
    fr: "Exemple de dialogue", de: "Beispieldialog", es: "Diálogo de ejemplo", it: "Dialogo di esempio",
    pt: "Diálogo de exemplo", "pt-BR": "Diálogo de exemplo", ru: "Пример диалога",
    ar: "مثال حوار", hi: "संवाद उदाहरण", id: "Contoh dialog",
  },
  "block.lorebookBefore": {
    ko: "로어북(앞)", "en-US": "Lorebook (before)", ja: "ロアブック（前）", zh: "设定集（前）",
    fr: "Codex (avant)", de: "Lorebook (davor)", es: "Compendio (antes)", it: "Codex (prima)",
    pt: "Compêndio (antes)", "pt-BR": "Compêndio (antes)", ru: "Лорбук (до)",
    ar: "كتاب المعرفة (قبل)", hi: "लोरबुक (पहले)", id: "Lorebook (sebelum)",
  },
  "block.lorebookAfter": {
    ko: "로어북(뒤)", "en-US": "Lorebook (after)", ja: "ロアブック（後）", zh: "设定集（后）",
    fr: "Codex (après)", de: "Lorebook (danach)", es: "Compendio (después)", it: "Codex (dopo)",
    pt: "Compêndio (depois)", "pt-BR": "Compêndio (depois)", ru: "Лорбук (после)",
    ar: "كتاب المعرفة (بعد)", hi: "लोरबुक (बाद)", id: "Lorebook (sesudah)",
  },
  "block.chatHistory": {
    ko: "대화 기록", "en-US": "Chat history", ja: "会話履歴", zh: "对话历史",
    fr: "Historique des discussions", de: "Chatverlauf", es: "Historial de chat", it: "Cronologia chat",
    pt: "Histórico de conversa", "pt-BR": "Histórico de conversa", ru: "История чата",
    ar: "سجل المحادثة", hi: "चैट इतिहास", id: "Riwayat obrolan",
  },
  "block.authorNote": {
    ko: "작가 노트", "en-US": "Author's note", ja: "作者ノート", zh: "作者注释",
    fr: "Note de l'auteur", de: "Autorennotiz", es: "Nota del autor", it: "Nota dell'autore",
    pt: "Nota do autor", "pt-BR": "Nota do autor", ru: "Заметка автора",
    ar: "ملاحظة المؤلف", hi: "लेखक की टिप्पणी", id: "Catatan penulis",
  },
  "block.postHistory": {
    ko: "후처리 지시", "en-US": "Post-history instructions", ja: "履歴後の指示", zh: "历史后指令",
    fr: "Instructions post-historique", de: "Anweisungen nach Verlauf", es: "Instrucciones post-historial",
    it: "Istruzioni post-cronologia", pt: "Instruções pós-histórico", "pt-BR": "Instruções pós-histórico",
    ru: "Инструкции после истории", ar: "تعليمات بعد السجل", hi: "इतिहास-पश्चात निर्देश",
    id: "Instruksi pasca-riwayat",
  },
  "block.custom": {
    ko: "사용자 정의", "en-US": "Custom", ja: "カスタム", zh: "自定义", fr: "Personnalisé",
    de: "Benutzerdefiniert", es: "Personalizado", it: "Personalizzato", pt: "Personalizado",
    "pt-BR": "Personalizado", ru: "Пользовательский", ar: "مخصص", hi: "कस्टम", id: "Khusus",
  },
  "persona.title": {
    ko: "페르소나 (내 캐릭터)", "en-US": "Personas (your character)", ja: "ペルソナ（あなたのキャラクター）",
    zh: "角色身份（你的角色）", fr: "Personas (votre personnage)", de: "Personas (deine Figur)",
    es: "Personas (tu personaje)", it: "Persona (il tuo personaggio)", pt: "Personas (a tua personagem)",
    "pt-BR": "Personas (seu personagem)", ru: "Персоны (ваш персонаж)", ar: "الشخصيات (شخصيتك)",
    hi: "पर्सोना (आपका पात्र)", id: "Persona (karakter Anda)",
  },
  "persona.add": {
    ko: "+ 페르소나 추가", "en-US": "+ Add persona", ja: "+ ペルソナを追加", zh: "+ 添加角色身份",
    fr: "+ Ajouter un persona", de: "+ Persona hinzufügen", es: "+ Añadir persona",
    it: "+ Aggiungi persona", pt: "+ Adicionar persona", "pt-BR": "+ Adicionar persona",
    ru: "+ Добавить персону", ar: "+ إضافة شخصية", hi: "+ पर्सोना जोड़ें", id: "+ Tambah persona",
  },
  "persona.keepOne": {
    ko: "최소 1개는 유지", "en-US": "Keep at least one", ja: "最低 1 つは残す", zh: "至少保留一个",
    fr: "Gardez-en au moins un", de: "Mindestens eine behalten", es: "Conserva al menos una",
    it: "Mantieni almeno una", pt: "Mantém pelo menos uma", "pt-BR": "Mantenha pelo menos uma",
    ru: "Оставьте хотя бы одну", ar: "احتفظ بواحدة على الأقل", hi: "कम से कम एक रखें", id: "Sisakan minimal satu",
  },
  "persona.avatarChange": {
    ko: "아바타 변경", "en-US": "Change avatar", ja: "アバターを変更", zh: "更换头像",
    fr: "Changer l'avatar", de: "Avatar ändern", es: "Cambiar avatar", it: "Cambia avatar",
    pt: "Mudar avatar", "pt-BR": "Alterar avatar", ru: "Сменить аватар", ar: "تغيير الصورة الرمزية",
    hi: "अवतार बदलें", id: "Ubah avatar",
  },
  "persona.avatarAlt": {
    ko: "아바타", "en-US": "Avatar", ja: "アバター", zh: "头像", fr: "Avatar", de: "Avatar",
    es: "Avatar", it: "Avatar", pt: "Avatar", "pt-BR": "Avatar", ru: "Аватар", ar: "الصورة الرمزية",
    hi: "अवतार", id: "Avatar",
  },
  "persona.namePlaceholder": {
    ko: "페르소나 이름 (@user)", "en-US": "Persona name (@user)", ja: "ペルソナ名（@user）",
    zh: "角色身份名称（@user）", fr: "Nom du persona (@user)", de: "Persona-Name (@user)",
    es: "Nombre del persona (@user)", it: "Nome del persona (@user)", pt: "Nome do persona (@user)",
    "pt-BR": "Nome do persona (@user)", ru: "Имя персоны (@user)", ar: "اسم الشخصية (@user)",
    hi: "पर्सोना नाम (@user)", id: "Nama persona (@user)",
  },
  "persona.descPlaceholder": {
    ko: "페르소나 설명 (프롬프트에 주입됨)", "en-US": "Persona description (injected into the prompt)",
    ja: "ペルソナの説明（プロンプトに挿入されます）", zh: "角色身份描述（注入到提示中）",
    fr: "Description du persona (injectée dans l'invite)", de: "Persona-Beschreibung (in den Prompt eingefügt)",
    es: "Descripción del persona (se inserta en el prompt)", it: "Descrizione del persona (inserita nel prompt)",
    pt: "Descrição do persona (injetada no prompt)", "pt-BR": "Descrição do persona (injetada no prompt)",
    ru: "Описание персоны (вставляется в промпт)", ar: "وصف الشخصية (يُدرَج في الموجّه)",
    hi: "पर्सोना विवरण (प्रॉम्प्ट में डाला जाता है)", id: "Deskripsi persona (disisipkan ke prompt)",
  },
  "prov.title": {
    ko: "AI 프로바이더", "en-US": "AI providers", ja: "AI プロバイダー", zh: "AI 提供商",
    fr: "Fournisseurs d'IA", de: "KI-Anbieter", es: "Proveedores de IA", it: "Provider IA",
    pt: "Fornecedores de IA", "pt-BR": "Provedores de IA", ru: "Провайдеры ИИ",
    ar: "مزوّدو الذكاء الاصطناعي", hi: "AI प्रदाता", id: "Penyedia AI",
  },
  "prov.empty": {
    ko: "등록된 프로바이더가 없습니다. 추가하고 API 키를 넣으면 대화를 시작할 수 있어요.",
    "en-US": "No providers yet. Add one and enter an API key to start chatting.",
    ja: "プロバイダーがまだありません。追加して API キーを入力すると会話を始められます。",
    zh: "尚无提供商。添加一个并输入 API 密钥即可开始对话。",
    fr: "Aucun fournisseur pour l'instant. Ajoutez-en un et saisissez une clé API pour discuter.",
    de: "Noch keine Anbieter. Füge einen hinzu und gib einen API-Schlüssel ein, um zu chatten.",
    es: "Aún no hay proveedores. Añade uno e introduce una clave API para empezar a chatear.",
    it: "Nessun provider. Aggiungine uno e inserisci una chiave API per iniziare a chattare.",
    pt: "Ainda não há fornecedores. Adicione um e introduza uma chave API para começar a conversar.",
    "pt-BR": "Ainda não há provedores. Adicione um e insira uma chave de API para começar a conversar.",
    ru: "Провайдеров пока нет. Добавьте провайдера и введите ключ API, чтобы начать общение.",
    ar: "لا يوجد مزوّدون بعد. أضف مزوّدًا وأدخل مفتاح API لبدء المحادثة.",
    hi: "अभी कोई प्रदाता नहीं। एक जोड़ें और API कुंजी डालें ताकि चैट शुरू हो सके।",
    id: "Belum ada penyedia. Tambahkan satu dan masukkan kunci API untuk mulai mengobrol.",
  },
  "prov.kindLocalCustom": {
    ko: "OpenAI 호환 (로컬/커스텀)", "en-US": "OpenAI-compatible (local/custom)",
    ja: "OpenAI 互換（ローカル/カスタム）", zh: "OpenAI 兼容（本地/自定义）",
    fr: "Compatible OpenAI (local/personnalisé)", de: "OpenAI-kompatibel (lokal/eigen)",
    es: "Compatible con OpenAI (local/personalizado)", it: "Compatibile OpenAI (locale/personalizzato)",
    pt: "Compatível com OpenAI (local/personalizado)", "pt-BR": "Compatível com OpenAI (local/personalizado)",
    ru: "Совместимый с OpenAI (локальный/свой)", ar: "متوافق مع OpenAI (محلي/مخصص)",
    hi: "OpenAI-संगत (लोकल/कस्टम)", id: "Kompatibel OpenAI (lokal/khusus)",
  },
  "prov.kindPlatform": {
    ko: "플랫폼 게이트웨이", "en-US": "Platform gateway", ja: "プラットフォームゲートウェイ",
    zh: "平台网关", fr: "Passerelle de la plateforme", de: "Plattform-Gateway",
    es: "Pasarela de la plataforma", it: "Gateway della piattaforma", pt: "Gateway da plataforma",
    "pt-BR": "Gateway da plataforma", ru: "Шлюз платформы", ar: "بوابة المنصة",
    hi: "प्लेटफ़ॉर्म गेटवे", id: "Gateway platform",
  },
  "prov.modelUnset": {
    ko: "모델 미지정", "en-US": "No model set", ja: "モデル未指定", zh: "未指定模型",
    fr: "Aucun modèle défini", de: "Kein Modell", es: "Sin modelo", it: "Nessun modello",
    pt: "Sem modelo", "pt-BR": "Sem modelo", ru: "Модель не задана", ar: "لم يُحدَّد نموذج",
    hi: "कोई मॉडल नहीं", id: "Model belum diatur",
  },
  "prov.keySet": {
    ko: "키 설정됨", "en-US": "Key set", ja: "キー設定済み", zh: "已设置密钥", fr: "Clé définie",
    de: "Schlüssel gesetzt", es: "Clave configurada", it: "Chiave impostata", pt: "Chave definida",
    "pt-BR": "Chave definida", ru: "Ключ задан", ar: "تم تعيين المفتاح", hi: "कुंजी सेट", id: "Kunci diatur",
  },
  "prov.keyNone": {
    ko: "키 없음", "en-US": "No key", ja: "キーなし", zh: "无密钥", fr: "Aucune clé",
    de: "Kein Schlüssel", es: "Sin clave", it: "Nessuna chiave", pt: "Sem chave", "pt-BR": "Sem chave",
    ru: "Нет ключа", ar: "لا يوجد مفتاح", hi: "कोई कुंजी नहीं", id: "Tanpa kunci",
  },
  "prov.add": {
    ko: "+ 프로바이더 추가", "en-US": "+ Add provider", ja: "+ プロバイダーを追加", zh: "+ 添加提供商",
    fr: "+ Ajouter un fournisseur", de: "+ Anbieter hinzufügen", es: "+ Añadir proveedor",
    it: "+ Aggiungi provider", pt: "+ Adicionar fornecedor", "pt-BR": "+ Adicionar provedor",
    ru: "+ Добавить провайдера", ar: "+ إضافة مزوّد", hi: "+ प्रदाता जोड़ें", id: "+ Tambah penyedia",
  },
  "prov.fieldName": {
    ko: "이름", "en-US": "Name", ja: "名前", zh: "名称", fr: "Nom", de: "Name", es: "Nombre",
    it: "Nome", pt: "Nome", "pt-BR": "Nome", ru: "Название", ar: "الاسم", hi: "नाम", id: "Nama",
  },
  "prov.fieldNamePlaceholder": {
    ko: "예: 내 OpenAI", "en-US": "e.g. My OpenAI", ja: "例: 私の OpenAI", zh: "例如：我的 OpenAI",
    fr: "ex. : Mon OpenAI", de: "z. B. Mein OpenAI", es: "p. ej., Mi OpenAI", it: "es. Il mio OpenAI",
    pt: "ex.: O meu OpenAI", "pt-BR": "ex.: Meu OpenAI", ru: "напр., Мой OpenAI",
    ar: "مثال: OpenAI الخاص بي", hi: "उदा. मेरा OpenAI", id: "mis. OpenAI saya",
  },
  "prov.fieldKind": {
    ko: "종류", "en-US": "Kind", ja: "種類", zh: "类型", fr: "Type", de: "Art", es: "Tipo",
    it: "Tipo", pt: "Tipo", "pt-BR": "Tipo", ru: "Тип", ar: "النوع", hi: "प्रकार", id: "Jenis",
  },
  "prov.fieldTransport": {
    ko: "전송 방식", "en-US": "Transport", ja: "通信方式", zh: "传输方式", fr: "Transport",
    de: "Übertragung", es: "Transporte", it: "Trasporto", pt: "Transporte", "pt-BR": "Transporte",
    ru: "Способ передачи", ar: "طريقة النقل", hi: "ट्रांसपोर्ट", id: "Transport",
  },
  "prov.transportDirect": {
    ko: "직접 호출", "en-US": "Direct", ja: "直接呼び出し", zh: "直接调用", fr: "Direct",
    de: "Direkt", es: "Directo", it: "Diretto", pt: "Direto", "pt-BR": "Direto",
    ru: "Напрямую", ar: "مباشر", hi: "सीधा", id: "Langsung",
  },
  "prov.transportProxy": {
    ko: "프록시 경유 (CORS 차단 우회)", "en-US": "Via proxy (bypass CORS block)",
    ja: "プロキシ経由（CORS 制限の回避）", zh: "经由代理（绕过 CORS 限制）",
    fr: "Via proxy (contourner le blocage CORS)", de: "Über Proxy (CORS-Sperre umgehen)",
    es: "Vía proxy (evitar bloqueo CORS)", it: "Via proxy (aggira il blocco CORS)",
    pt: "Via proxy (contornar bloqueio CORS)", "pt-BR": "Via proxy (contornar bloqueio CORS)",
    ru: "Через прокси (обход блокировки CORS)", ar: "عبر وسيط (تجاوز حظر CORS)",
    hi: "प्रॉक्सी के माध्यम से (CORS ब्लॉक बायपास)", id: "Lewat proxy (lewati blokir CORS)",
  },
  "prov.transportGateway": {
    ko: "게이트웨이 (유료)", "en-US": "Gateway (paid)", ja: "ゲートウェイ（有料）",
    zh: "网关（付费）", fr: "Passerelle (payant)", de: "Gateway (kostenpflichtig)",
    es: "Pasarela (de pago)", it: "Gateway (a pagamento)", pt: "Gateway (pago)",
    "pt-BR": "Gateway (pago)", ru: "Шлюз (платно)", ar: "بوابة (مدفوعة)",
    hi: "गेटवे (सशुल्क)", id: "Gateway (berbayar)",
  },
  "prov.proxyUrl": {
    ko: "프록시 URL (선택)", "en-US": "Proxy URL (optional)", ja: "プロキシ URL（任意）",
    zh: "代理 URL（可选）", fr: "URL du proxy (facultatif)", de: "Proxy-URL (optional)",
    es: "URL del proxy (opcional)", it: "URL del proxy (facoltativo)", pt: "URL do proxy (opcional)",
    "pt-BR": "URL do proxy (opcional)", ru: "URL прокси (необязательно)", ar: "رابط الوسيط (اختياري)",
    hi: "प्रॉक्सी URL (वैकल्पिक)", id: "URL proxy (opsional)",
  },
  "prov.baseUrl": {
    ko: "Base URL", "en-US": "Base URL", ja: "ベース URL", zh: "基础 URL", fr: "URL de base",
    de: "Basis-URL", es: "URL base", it: "URL base", pt: "URL base", "pt-BR": "URL base",
    ru: "Базовый URL", ar: "الرابط الأساسي", hi: "बेस URL", id: "URL dasar",
  },
  "prov.proxyUrlPlaceholder": {
    ko: "비우면 빌드 설정의 프록시 사용", "en-US": "Leave blank to use the build's proxy",
    ja: "空欄ならビルド設定のプロキシを使用", zh: "留空则使用构建配置的代理",
    fr: "Laissez vide pour utiliser le proxy de la build", de: "Leer lassen, um den Build-Proxy zu nutzen",
    es: "Déjalo vacío para usar el proxy de la compilación", it: "Lascia vuoto per usare il proxy della build",
    pt: "Deixe em branco para usar o proxy da build", "pt-BR": "Deixe em branco para usar o proxy do build",
    ru: "Оставьте пустым, чтобы использовать прокси сборки", ar: "اتركه فارغًا لاستخدام وسيط البناء",
    hi: "बिल्ड का प्रॉक्सी उपयोग करने के लिए खाली छोड़ें", id: "Kosongkan untuk memakai proxy build",
  },
  "prov.baseUrlPlaceholder": {
    ko: "http://localhost:11434/v1", "en-US": "http://localhost:11434/v1",
    ja: "http://localhost:11434/v1", zh: "http://localhost:11434/v1", fr: "http://localhost:11434/v1",
    de: "http://localhost:11434/v1", es: "http://localhost:11434/v1", it: "http://localhost:11434/v1",
    pt: "http://localhost:11434/v1", "pt-BR": "http://localhost:11434/v1", ru: "http://localhost:11434/v1",
    ar: "http://localhost:11434/v1", hi: "http://localhost:11434/v1", id: "http://localhost:11434/v1",
  },
  "prov.defaultModel": {
    ko: "기본 모델", "en-US": "Default model", ja: "デフォルトモデル", zh: "默认模型",
    fr: "Modèle par défaut", de: "Standardmodell", es: "Modelo predeterminado", it: "Modello predefinito",
    pt: "Modelo predefinido", "pt-BR": "Modelo padrão", ru: "Модель по умолчанию",
    ar: "النموذج الافتراضي", hi: "डिफ़ॉल्ट मॉडल", id: "Model bawaan",
  },
  "prov.modelPlaceholder": {
    ko: "예: gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    "en-US": "e.g. gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    ja: "例: gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    zh: "例如：gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    fr: "ex. : gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    de: "z. B. gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    es: "p. ej., gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    it: "es. gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    pt: "ex.: gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    "pt-BR": "ex.: gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    ru: "напр., gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    ar: "مثال: gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    hi: "उदा. gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
    id: "mis. gpt-4o-mini / claude-3-5-sonnet / gemini-1.5-flash",
  },
  "prov.fetch": {
    ko: "불러오기", "en-US": "Fetch", ja: "取得", zh: "获取", fr: "Charger",
    de: "Abrufen", es: "Obtener", it: "Carica", pt: "Obter", "pt-BR": "Buscar",
    ru: "Загрузить", ar: "جلب", hi: "लाएँ", id: "Ambil",
  },
  "prov.fetching": {
    ko: "불러오는 중...", "en-US": "Fetching...", ja: "取得中...", zh: "获取中...", fr: "Chargement...",
    de: "Wird abgerufen...", es: "Obteniendo...", it: "Caricamento...", pt: "A obter...", "pt-BR": "Buscando...",
    ru: "Загрузка...", ar: "جارٍ الجلب...", hi: "लाया जा रहा है...", id: "Mengambil...",
  },
  "prov.fetchTitle": {
    ko: "키/URL로 모델 목록 불러오기", "en-US": "Fetch model list with key/URL",
    ja: "キー/URL でモデル一覧を取得", zh: "用密钥/URL 获取模型列表",
    fr: "Charger la liste des modèles avec la clé/URL", de: "Modellliste mit Schlüssel/URL abrufen",
    es: "Obtener la lista de modelos con clave/URL", it: "Carica l'elenco modelli con chiave/URL",
    pt: "Obter lista de modelos com chave/URL", "pt-BR": "Buscar lista de modelos com chave/URL",
    ru: "Загрузить список моделей по ключу/URL", ar: "جلب قائمة النماذج بالمفتاح/الرابط",
    hi: "कुंजी/URL से मॉडल सूची लाएँ", id: "Ambil daftar model dengan kunci/URL",
  },
  "prov.fetchFailed": {
    ko: "모델 목록을 가져오지 못했어요(미지원이거나 키/URL 확인 필요).",
    "en-US": "Couldn't fetch the model list (unsupported, or check key/URL).",
    ja: "モデル一覧を取得できませんでした（非対応、またはキー/URL を確認してください）。",
    zh: "无法获取模型列表（不支持，或请检查密钥/URL）。",
    fr: "Impossible de charger la liste des modèles (non pris en charge, ou vérifiez la clé/URL).",
    de: "Modellliste konnte nicht abgerufen werden (nicht unterstützt oder Schlüssel/URL prüfen).",
    es: "No se pudo obtener la lista de modelos (no compatible o revisa clave/URL).",
    it: "Impossibile caricare l'elenco modelli (non supportato o verifica chiave/URL).",
    pt: "Não foi possível obter a lista de modelos (não suportado ou verifique chave/URL).",
    "pt-BR": "Não foi possível buscar a lista de modelos (não suportado ou verifique chave/URL).",
    ru: "Не удалось загрузить список моделей (не поддерживается или проверьте ключ/URL).",
    ar: "تعذّر جلب قائمة النماذج (غير مدعوم، أو تحقق من المفتاح/الرابط).",
    hi: "मॉडल सूची नहीं ला सके (असमर्थित, या कुंजी/URL जाँचें)।",
    id: "Tidak dapat mengambil daftar model (tidak didukung, atau periksa kunci/URL).",
  },
  "prov.fetchUnsupported": {
    ko: "이 프로바이더는 채팅 모델 목록을 제공하지 않습니다.",
    "en-US": "This provider does not expose a chat model list.",
    ja: "This provider does not expose a chat model list.",
    zh: "This provider does not expose a chat model list.",
    fr: "This provider does not expose a chat model list.",
    de: "This provider does not expose a chat model list.",
    es: "This provider does not expose a chat model list.",
    it: "This provider does not expose a chat model list.",
    pt: "This provider does not expose a chat model list.",
    "pt-BR": "This provider does not expose a chat model list.",
    ru: "This provider does not expose a chat model list.",
    ar: "This provider does not expose a chat model list.",
    hi: "This provider does not expose a chat model list.",
    id: "This provider does not expose a chat model list.",
  },
  "prov.baseUrlRequired": {
    ko: "{name}에는 Base URL이 필요합니다.",
    "en-US": "{name} requires a base URL.",
    ja: "{name} requires a base URL.",
    zh: "{name} requires a base URL.",
    fr: "{name} requires a base URL.",
    de: "{name} requires a base URL.",
    es: "{name} requires a base URL.",
    it: "{name} requires a base URL.",
    pt: "{name} requires a base URL.",
    "pt-BR": "{name} requires a base URL.",
    ru: "{name} requires a base URL.",
    ar: "{name} requires a base URL.",
    hi: "{name} requires a base URL.",
    id: "{name} requires a base URL.",
  },
  "prov.deleteConfirm": {
    ko: "\"{name}\" 프로바이더를 삭제할까요? 저장된 키도 함께 제거됩니다.",
    "en-US": "Delete provider \"{name}\"? Its stored key will also be removed.",
    ja: "Delete provider \"{name}\"? Its stored key will also be removed.",
    zh: "Delete provider \"{name}\"? Its stored key will also be removed.",
    fr: "Delete provider \"{name}\"? Its stored key will also be removed.",
    de: "Delete provider \"{name}\"? Its stored key will also be removed.",
    es: "Delete provider \"{name}\"? Its stored key will also be removed.",
    it: "Delete provider \"{name}\"? Its stored key will also be removed.",
    pt: "Delete provider \"{name}\"? Its stored key will also be removed.",
    "pt-BR": "Delete provider \"{name}\"? Its stored key will also be removed.",
    ru: "Delete provider \"{name}\"? Its stored key will also be removed.",
    ar: "Delete provider \"{name}\"? Its stored key will also be removed.",
    hi: "Delete provider \"{name}\"? Its stored key will also be removed.",
    id: "Delete provider \"{name}\"? Its stored key will also be removed.",
  },
  "prov.modelsSelect": {
    ko: "{n}개 모델 — 선택", "en-US": "{n} models — choose", ja: "{n} 個のモデル — 選択",
    zh: "{n} 个模型 — 选择", fr: "{n} modèles — choisir", de: "{n} Modelle — auswählen",
    es: "{n} modelos — elegir", it: "{n} modelli — scegli", pt: "{n} modelos — escolher",
    "pt-BR": "{n} modelos — escolher", ru: "{n} моделей — выбрать", ar: "{n} نماذج — اختر",
    hi: "{n} मॉडल — चुनें", id: "{n} model — pilih",
  },
  "prov.apiKey": {
    ko: "API 키", "en-US": "API key", ja: "API キー", zh: "API 密钥", fr: "Clé API",
    de: "API-Schlüssel", es: "Clave API", it: "Chiave API", pt: "Chave API", "pt-BR": "Chave de API",
    ru: "Ключ API", ar: "مفتاح API", hi: "API कुंजी", id: "Kunci API",
  },
  "prov.apiKeyPlaceholder": {
    ko: "암호화되어 기기에만 저장됩니다", "en-US": "Stored encrypted on this device only",
    ja: "暗号化されてこの端末にのみ保存されます", zh: "加密后仅保存在本设备",
    fr: "Stockée chiffrée sur cet appareil uniquement", de: "Verschlüsselt nur auf diesem Gerät gespeichert",
    es: "Se guarda cifrada solo en este dispositivo", it: "Salvata cifrata solo su questo dispositivo",
    pt: "Guardada cifrada apenas neste dispositivo", "pt-BR": "Armazenada criptografada apenas neste dispositivo",
    ru: "Хранится в зашифрованном виде только на этом устройстве", ar: "تُخزَّن مشفّرة على هذا الجهاز فقط",
    hi: "केवल इस डिवाइस पर एन्क्रिप्टेड संग्रहीत", id: "Disimpan terenkripsi hanya di perangkat ini",
  },
  "prov.apiKeyKeepPlaceholder": {
    ko: "비워두면 기존 키를 유지합니다",
    "en-US": "Leave blank to keep the existing key",
  },
  "settings.language": {
    ko: "언어", "en-US": "Language", ja: "言語", zh: "语言", fr: "Langue",
    de: "Sprache", es: "Idioma", it: "Lingua", pt: "Idioma", "pt-BR": "Idioma",
    ru: "Язык", ar: "اللغة", hi: "भाषा", id: "Bahasa",
  },
  "settings.languageHint": {
    ko: "인터페이스 표시 언어. 일부 화면은 아직 번역 중입니다.",
    "en-US": "Interface display language. Some screens are still being translated.",
    ja: "インターフェースの表示言語。一部の画面はまだ翻訳中です。",
    zh: "界面显示语言。部分页面仍在翻译中。",
    fr: "Langue d'affichage de l'interface. Certains écrans sont encore en cours de traduction.",
    de: "Anzeigesprache der Oberfläche. Einige Bildschirme werden noch übersetzt.",
    es: "Idioma de la interfaz. Algunas pantallas aún se están traduciendo.",
    it: "Lingua dell'interfaccia. Alcune schermate sono ancora in fase di traduzione.",
    pt: "Idioma de apresentação da interface. Alguns ecrãs ainda estão a ser traduzidos.",
    "pt-BR": "Idioma de exibição da interface. Algumas telas ainda estão sendo traduzidas.",
    ru: "Язык интерфейса. Некоторые экраны ещё переводятся.",
    ar: "لغة عرض الواجهة. لا تزال بعض الشاشات قيد الترجمة.",
    hi: "इंटरफ़ेस की प्रदर्शन भाषा। कुछ स्क्रीन का अनुवाद अभी जारी है।",
    id: "Bahasa tampilan antarmuka. Beberapa layar masih diterjemahkan.",
  },
  "settings.display.title": {
    ko: "표시", "en-US": "Display", ja: "表示", zh: "显示", fr: "Affichage",
    de: "Anzeige", es: "Visualización", it: "Visualizzazione", pt: "Apresentação", "pt-BR": "Exibição",
    ru: "Отображение", ar: "العرض", hi: "प्रदर्शन", id: "Tampilan",
  },
  "settings.theme": {
    ko: "테마", "en-US": "Theme", ja: "テーマ", zh: "主题", fr: "Thème",
    de: "Design", es: "Tema", it: "Tema", pt: "Tema", "pt-BR": "Tema",
    ru: "Тема", ar: "السمة", hi: "थीम", id: "Tema",
  },
  "settings.theme.system": {
    ko: "시스템", "en-US": "System", ja: "システム", zh: "跟随系统", fr: "Système",
    de: "System", es: "Sistema", it: "Sistema", pt: "Sistema", "pt-BR": "Sistema",
    ru: "Системная", ar: "النظام", hi: "सिस्टम", id: "Sistem",
  },
  "settings.theme.dark": {
    ko: "다크", "en-US": "Dark", ja: "ダーク", zh: "深色", fr: "Sombre",
    de: "Dunkel", es: "Oscuro", it: "Scuro", pt: "Escuro", "pt-BR": "Escuro",
    ru: "Тёмная", ar: "داكن", hi: "गहरा", id: "Gelap",
  },
  "settings.theme.light": {
    ko: "라이트", "en-US": "Light", ja: "ライト", zh: "浅色", fr: "Clair",
    de: "Hell", es: "Claro", it: "Chiaro", pt: "Claro", "pt-BR": "Claro",
    ru: "Светлая", ar: "فاتح", hi: "हल्का", id: "Terang",
  },
  "settings.perf.title": {
    ko: "성능 (저사양 기기 보호)", "en-US": "Performance (protect low-end devices)",
    ja: "パフォーマンス（低スペック端末の保護）", zh: "性能（保护低端设备）",
    fr: "Performances (protéger les appareils peu puissants)",
    de: "Leistung (schwache Geräte schonen)",
    es: "Rendimiento (proteger dispositivos de gama baja)",
    it: "Prestazioni (proteggi i dispositivi meno potenti)",
    pt: "Desempenho (proteger dispositivos modestos)",
    "pt-BR": "Desempenho (proteger dispositivos modestos)",
    ru: "Производительность (защита слабых устройств)",
    ar: "الأداء (حماية الأجهزة الضعيفة)",
    hi: "प्रदर्शन (कम क्षमता वाले उपकरणों की सुरक्षा)",
    id: "Performa (lindungi perangkat kelas bawah)",
  },
  "settings.perf.incMarkdown": {
    ko: "스트리밍 중 증분 마크다운", "en-US": "Incremental markdown while streaming",
    ja: "ストリーミング中の逐次マークダウン", zh: "流式传输时增量渲染 Markdown",
    fr: "Markdown incrémental pendant le streaming", de: "Inkrementelles Markdown beim Streaming",
    es: "Markdown incremental durante el streaming", it: "Markdown incrementale durante lo streaming",
    pt: "Markdown incremental durante o streaming", "pt-BR": "Markdown incremental durante o streaming",
    ru: "Инкрементальный Markdown при потоковой передаче", ar: "تحويل Markdown تدريجيًا أثناء البث",
    hi: "स्ट्रीमिंग के दौरान इंक्रीमेंटल मार्कडाउन", id: "Markdown bertahap saat streaming",
  },
  "settings.perf.incMarkdownHint": {
    ko: "끄면 스트리밍은 가벼운 텍스트로, 완료 시 1회 렌더. iPhone 6s+ 등에서는 끄는 것을 권장.",
    "en-US": "When off, streaming shows light text and renders once on completion. Recommended off on iPhone 6s+ and similar.",
    ja: "オフにするとストリーミングは軽量テキストで表示し、完了時に一度だけレンダリング。iPhone 6s+ などではオフを推奨。",
    zh: "关闭后，流式传输显示纯文本，完成时渲染一次。iPhone 6s+ 等设备建议关闭。",
    fr: "Désactivé, le streaming affiche du texte léger et effectue le rendu une fois terminé. Recommandé désactivé sur iPhone 6s+ et similaires.",
    de: "Wenn aus, zeigt das Streaming einfachen Text und rendert einmal am Ende. Auf iPhone 6s+ und Ähnlichem empfohlen.",
    es: "Si está desactivado, el streaming muestra texto ligero y se renderiza una vez al terminar. Recomendado desactivado en iPhone 6s+ y similares.",
    it: "Se disattivato, lo streaming mostra testo leggero e renderizza una volta al termine. Consigliato disattivato su iPhone 6s+ e simili.",
    pt: "Quando desligado, o streaming mostra texto simples e renderiza uma vez no fim. Recomendado desligado em iPhone 6s+ e semelhantes.",
    "pt-BR": "Quando desligado, o streaming mostra texto simples e renderiza uma vez ao concluir. Recomendado desligado em iPhone 6s+ e similares.",
    ru: "Когда выключено, поток показывает простой текст и рендерится один раз по завершении. Рекомендуется выключить на iPhone 6s+ и подобных.",
    ar: "عند الإيقاف، يعرض البث نصًا بسيطًا ويُعرَض مرة واحدة عند الاكتمال. يُنصح بإيقافه على iPhone 6s+ والأجهزة المماثلة.",
    hi: "बंद होने पर स्ट्रीमिंग सरल टेक्स्ट दिखाती है और पूरा होने पर एक बार रेंडर करती है। iPhone 6s+ आदि पर बंद रखने की सलाह।",
    id: "Saat mati, streaming menampilkan teks ringan dan dirender sekali saat selesai. Disarankan mati di iPhone 6s+ dan sejenisnya.",
  },
  "settings.perf.forceNonStream": {
    ko: "비스트리밍 강제", "en-US": "Force non-streaming", ja: "非ストリーミングを強制",
    zh: "强制非流式", fr: "Forcer le mode sans streaming", de: "Nicht-Streaming erzwingen",
    es: "Forzar sin streaming", it: "Forza senza streaming", pt: "Forçar sem streaming",
    "pt-BR": "Forçar sem streaming", ru: "Принудительно без потока", ar: "فرض عدم البث",
    hi: "नॉन-स्ट्रीमिंग बाध्य करें", id: "Paksa tanpa streaming",
  },
  "settings.perf.forceNonStreamHint": {
    ko: "스트리밍이 불안정한 환경에서 응답을 한 번에 받습니다.",
    "en-US": "Receive the whole response at once where streaming is unstable.",
    ja: "ストリーミングが不安定な環境で応答を一度に受け取ります。",
    zh: "在流式不稳定的环境中一次性接收完整回复。",
    fr: "Recevez toute la réponse d'un coup là où le streaming est instable.",
    de: "Empfange die gesamte Antwort auf einmal, wenn Streaming instabil ist.",
    es: "Recibe toda la respuesta de una vez donde el streaming es inestable.",
    it: "Ricevi l'intera risposta in una volta dove lo streaming è instabile.",
    pt: "Receba toda a resposta de uma vez quando o streaming for instável.",
    "pt-BR": "Receba toda a resposta de uma vez quando o streaming for instável.",
    ru: "Получайте весь ответ сразу там, где потоковая передача нестабильна.",
    ar: "استلم الرد كاملًا دفعة واحدة حيث يكون البث غير مستقر.",
    hi: "जहाँ स्ट्रीमिंग अस्थिर हो वहाँ पूरा उत्तर एक साथ प्राप्त करें।",
    id: "Terima seluruh respons sekaligus saat streaming tidak stabil.",
  },
  "settings.perf.reducedMotion": {
    ko: "모션 줄이기", "en-US": "Reduce motion", ja: "モーションを減らす", zh: "减少动态效果",
    fr: "Réduire les animations", de: "Bewegung reduzieren", es: "Reducir el movimiento",
    it: "Riduci le animazioni", pt: "Reduzir movimento", "pt-BR": "Reduzir movimento",
    ru: "Уменьшить анимацию", ar: "تقليل الحركة", hi: "मोशन कम करें", id: "Kurangi animasi",
  },
  "settings.perf.reducedMotionHint": {
    ko: "애니메이션·블러를 줄여 저전력 기기에서 부드럽게.",
    "en-US": "Reduce animations and blur for smoother performance on low-power devices.",
    ja: "アニメーションやぼかしを減らし、低電力端末でも滑らかに。",
    zh: "减少动画与模糊，让低功耗设备更流畅。",
    fr: "Réduisez les animations et le flou pour plus de fluidité sur les appareils peu puissants.",
    de: "Reduziere Animationen und Unschärfe für flüssigere Leistung auf schwachen Geräten.",
    es: "Reduce animaciones y desenfoque para mayor fluidez en dispositivos de bajo consumo.",
    it: "Riduci animazioni e sfocature per una maggiore fluidità sui dispositivi a basso consumo.",
    pt: "Reduz animações e desfoque para maior fluidez em dispositivos de baixo consumo.",
    "pt-BR": "Reduz animações e desfoque para mais fluidez em dispositivos de baixo consumo.",
    ru: "Уменьшите анимацию и размытие для плавности на маломощных устройствах.",
    ar: "قلّل الحركة والتمويه لأداء أكثر سلاسة على الأجهزة منخفضة الطاقة.",
    hi: "कम-शक्ति वाले उपकरणों पर सहज प्रदर्शन के लिए एनिमेशन और ब्लर कम करें।",
    id: "Kurangi animasi dan blur untuk performa lebih mulus di perangkat berdaya rendah.",
  },
  "settings.identity.title": {
    ko: "신원", "en-US": "Identity", ja: "アカウント", zh: "身份", fr: "Identité",
    de: "Identität", es: "Identidad", it: "Identità", pt: "Identidade", "pt-BR": "Identidade",
    ru: "Личность", ar: "الهوية", hi: "पहचान", id: "Identitas",
  },
  "settings.identity.current": {
    ko: "현재 신원", "en-US": "Current identity", ja: "現在のアカウント", zh: "当前身份",
    fr: "Identité actuelle", de: "Aktuelle Identität", es: "Identidad actual", it: "Identità attuale",
    pt: "Identidade atual", "pt-BR": "Identidade atual", ru: "Текущая личность",
    ar: "الهوية الحالية", hi: "वर्तमान पहचान", id: "Identitas saat ini",
  },
  "settings.identity.anonymous": {
    ko: "익명 (로컬 전용)", "en-US": "Anonymous (local only)", ja: "匿名（ローカルのみ）",
    zh: "匿名（仅本地）", fr: "Anonyme (local uniquement)", de: "Anonym (nur lokal)",
    es: "Anónimo (solo local)", it: "Anonimo (solo locale)", pt: "Anónimo (apenas local)",
    "pt-BR": "Anônimo (apenas local)", ru: "Аноним (только локально)", ar: "مجهول (محلي فقط)",
    hi: "गुमनाम (केवल लोकल)", id: "Anonim (hanya lokal)",
  },
  "settings.identity.google": {
    ko: "Google 연결됨", "en-US": "Google connected", ja: "Google 連携済み", zh: "已连接 Google",
    fr: "Google connecté", de: "Google verbunden", es: "Google conectado", it: "Google collegato",
    pt: "Google ligado", "pt-BR": "Google conectado", ru: "Google подключён",
    ar: "تم ربط Google", hi: "Google जुड़ा हुआ", id: "Google terhubung",
  },
  "settings.identity.platform": {
    ko: "플랫폼 계정", "en-US": "Platform account", ja: "プラットフォームアカウント",
    zh: "平台账户", fr: "Compte de la plateforme", de: "Plattform-Konto", es: "Cuenta de la plataforma",
    it: "Account della piattaforma", pt: "Conta da plataforma", "pt-BR": "Conta da plataforma",
    ru: "Аккаунт платформы", ar: "حساب المنصة", hi: "प्लेटफ़ॉर्म खाता", id: "Akun platform",
  },
  "settings.identity.hubConnected": {
    ko: "허브에 연결되었습니다.", "en-US": "Connected to Hub.", ja: "Connected to Hub.",
    zh: "Connected to Hub.", fr: "Connected to Hub.", de: "Connected to Hub.", es: "Connected to Hub.",
    it: "Connected to Hub.", pt: "Connected to Hub.", "pt-BR": "Connected to Hub.",
    ru: "Connected to Hub.", ar: "Connected to Hub.", hi: "Connected to Hub.", id: "Connected to Hub.",
  },
  "settings.identity.hubFail": {
    ko: "연결 실패: {{error}}", "en-US": "Connection failed: {{error}}", ja: "接続失敗: {{error}}",
    zh: "连接失败: {{error}}", fr: "Échec de la connexion : {{error}}", de: "Verbindung fehlgeschlagen: {{error}}", es: "Error de conexión: {{error}}",
    it: "Connessione fallita: {{error}}", pt: "Falha na conexão: {{error}}", "pt-BR": "Falha na conexão: {{error}}",
    ru: "Ошибка подключения: {{error}}", ar: "فشل الاتصال: {{error}}", hi: "कनेक्शन विफल: {{error}}", id: "Koneksi gagal: {{error}}",
  },
  "settings.identity.profileSaved": {
    ko: "프로필이 저장되었습니다.", "en-US": "Profile saved.", ja: "プロフィールを保存しました。",
    zh: "个人资料已保存。", fr: "Profil enregistré.", de: "Profil gespeichert.", es: "Perfil guardado.",
    it: "Profilo salvato.", pt: "Perfil salvo.", "pt-BR": "Perfil salvo.",
    ru: "Профиль сохранен.", ar: "تم حفظ الملف الشخصي.", hi: "प्रोफाइल सहेजा गया।", id: "Profil disimpan.",
  },
  "settings.identity.profileFail": {
    ko: "저장 실패: {{error}}", "en-US": "Save failed: {{error}}", ja: "保存失敗: {{error}}",
    zh: "保存失败: {{error}}", fr: "Échec de l'enregistrement : {{error}}", de: "Speichern fehlgeschlagen: {{error}}", es: "Error al guardar: {{error}}",
    it: "Salvataggio fallito: {{error}}", pt: "Falha ao salvar: {{error}}", "pt-BR": "Falha ao salvar: {{error}}",
    ru: "Ошибка сохранения: {{error}}", ar: "فشل الحفظ: {{error}}", hi: "सहेजना विफल: {{error}}", id: "Gagal menyimpan: {{error}}",
  },
  "sync.conflictTitle": {
    ko: "동기화 충돌 해결", "en-US": "Resolve Sync Conflict", ja: "同期競合の解決", zh: "解决同步冲突",
    fr: "Résoudre le conflit de synchronisation", de: "Synchronisierungskonflikt lösen", es: "Resolver conflicto de sincronización", it: "Risolvi conflitto di sincronizzazione",
    pt: "Resolver conflito de sincronização", "pt-BR": "Resolver conflito de sincronização", ru: "Разрешение конфликта синхронизации", ar: "حل تعارض المزامنة",
    hi: "सिंक विरोध हल करें", id: "Selesaikan Konflik Sinkronisasi",
  },
  "sync.conflictDesc": {
    ko: "로컬과 클라우드의 내용이 다릅니다. 어떻게 병합할지 선택해주세요.", "en-US": "Local and cloud contents differ. Please choose how to merge.", ja: "ローカルとクラウドのコンテンツが異なります。マージ方法を選択してください。",
    zh: "本地和云端内容不同。请选择合并方式。", fr: "Le contenu local et le contenu cloud diffèrent. Veuillez choisir comment fusionner.", de: "Lokale und Cloud-Inhalte unterscheiden sich. Bitte wählen Sie, wie zusammengeführt werden soll.", es: "El contenido local y de la nube difieren. Por favor elija cómo fusionar.",
    it: "Il contenuto locale e quello cloud differiscono. Scegli come unire.", pt: "Os conteúdos local e na nuvem diferem. Por favor, escolha como mesclar.", "pt-BR": "Os conteúdos local e na nuvem diferem. Por favor, escolha como mesclar.",
    ru: "Локальное и облачное содержимое различаются. Пожалуйста, выберите способ объединения.", ar: "تختلف المحتويات المحلية والسحابية. يرجى اختيار كيفية الدمج.", hi: "स्थानीय और क्लाउड सामग्री अलग-अलग है। कृपया चुनें कि कैसे मर्ज करें।", id: "Konten lokal dan awan berbeda. Silakan pilih cara menggabungkan.",
  },
  "sync.conflictCancelled": {
    ko: "충돌 해결이 취소되었습니다.", "en-US": "Conflict resolution cancelled.", ja: "競合の解決がキャンセルされました。",
    zh: "解决冲突已取消。", fr: "La résolution du conflit a été annulée.", de: "Konfliktlösung abgebrochen.", es: "Resolución de conflictos cancelada.",
    it: "Risoluzione dei conflitti annullata.", pt: "Resolução de conflito cancelada.", "pt-BR": "Resolução de conflito cancelada.",
    ru: "Разрешение конфликтов отменено.", ar: "تم إلغاء حل النزاع.", hi: "संघर्ष समाधान रद्द कर दिया गया।", id: "Penyelesaian konflik dibatalkan.",
  },
  "sync.failed": {
    ko: "동기화 실패: {{error}}", "en-US": "Sync failed: {{error}}", ja: "同期失敗: {{error}}",
    zh: "同步失败: {{error}}", fr: "Échec de la synchronisation : {{error}}", de: "Synchronisierung fehlgeschlagen: {{error}}", es: "Error de sincronización: {{error}}",
    it: "Sincronizzazione fallita: {{error}}", pt: "Falha na sincronização: {{error}}", "pt-BR": "Falha na sincronização: {{error}}",
    ru: "Ошибка синхронизации: {{error}}", ar: "فشل المزامنة: {{error}}", hi: "सिंक विफल: {{error}}", id: "Sinkronisasi gagal: {{error}}",
  },
  "settings.identity.note": {
    ko: "기기 간 동기화는 위 \"동기화\"에서 Google Drive 또는 파일로 합칠 수 있어요. 익명 사용자도 모든 롤플레잉 기능을 그대로 씁니다. API 키는 어떤 경우에도 동기화에 포함되지 않습니다.",
    "en-US": "Sync across devices via Google Drive or a file in \"Sync\" above. Anonymous users get every roleplay feature. API keys are never included in sync.",
    ja: "端末間の同期は上の「同期」から Google ドライブまたはファイルで行えます。匿名ユーザーもすべてのロールプレイ機能を利用できます。API キーが同期に含まれることはありません。",
    zh: "可在上方“同步”中通过 Google Drive 或文件进行跨设备同步。匿名用户也能使用全部角色扮演功能。API 密钥永远不会包含在同步中。",
    fr: "Synchronisez entre appareils via Google Drive ou un fichier dans « Synchronisation » ci-dessus. Les utilisateurs anonymes ont toutes les fonctions de jeu de rôle. Les clés API ne sont jamais incluses dans la synchronisation.",
    de: "Geräteübergreifend über Google Drive oder eine Datei unter „Synchronisierung“ oben. Anonyme Nutzer erhalten alle Rollenspiel-Funktionen. API-Schlüssel werden nie synchronisiert.",
    es: "Sincroniza entre dispositivos con Google Drive o un archivo en «Sincronización» arriba. Los usuarios anónimos tienen todas las funciones de rol. Las claves API nunca se incluyen en la sincronización.",
    it: "Sincronizza tra dispositivi con Google Drive o un file in «Sincronizzazione» sopra. Gli utenti anonimi hanno tutte le funzioni di gioco di ruolo. Le chiavi API non sono mai incluse nella sincronizzazione.",
    pt: "Sincronize entre dispositivos via Google Drive ou um ficheiro em \"Sincronização\" acima. Utilizadores anónimos têm todas as funcionalidades de roleplay. As chaves de API nunca são incluídas na sincronização.",
    "pt-BR": "Sincronize entre dispositivos via Google Drive ou um arquivo em \"Sincronização\" acima. Usuários anônimos têm todos os recursos de roleplay. As chaves de API nunca são incluídas na sincronização.",
    ru: "Синхронизация между устройствами через Google Drive или файл в разделе «Синхронизация» выше. Анонимные пользователи получают все функции ролевой игры. Ключи API никогда не включаются в синхронизацию.",
    ar: "زامِن بين الأجهزة عبر Google Drive أو ملف من \"المزامنة\" أعلاه. يحصل المستخدمون المجهولون على كل ميزات تقمص الأدوار. لا تُضمَّن مفاتيح API في المزامنة أبدًا.",
    hi: "ऊपर \"सिंक\" में Google Drive या फ़ाइल से उपकरणों के बीच सिंक करें। गुमनाम उपयोगकर्ताओं को भी सभी रोलप्ले सुविधाएँ मिलती हैं। API कुंजियाँ कभी सिंक में शामिल नहीं होतीं।",
    id: "Sinkronkan antar-perangkat via Google Drive atau berkas di \"Sinkronisasi\" di atas. Pengguna anonim mendapat semua fitur roleplay. Kunci API tidak pernah disertakan dalam sinkronisasi.",
  },
  "settings.content.title": {
    ko: "콘텐츠", "en-US": "Content", ja: "コンテンツ", zh: "内容", fr: "Contenu",
    de: "Inhalte", es: "Contenido", it: "Contenuti", pt: "Conteúdo", "pt-BR": "Conteúdo",
    ru: "Контент", ar: "المحتوى", hi: "सामग्री", id: "Konten",
  },
  "settings.content.ageVerify": {
    ko: "연령 확인", "en-US": "Age verification", ja: "年齢確認", zh: "年龄确认",
    fr: "Vérification de l'âge", de: "Altersbestätigung", es: "Verificación de edad",
    it: "Verifica dell'età", pt: "Verificação de idade", "pt-BR": "Verificação de idade",
    ru: "Подтверждение возраста", ar: "التحقق من العمر", hi: "आयु सत्यापन", id: "Verifikasi usia",
  },
  "settings.content.verified": {
    ko: "확인됨 (성인)", "en-US": "Verified (adult)", ja: "確認済み（成人）", zh: "已确认（成人）",
    fr: "Vérifié (adulte)", de: "Bestätigt (volljährig)", es: "Verificado (adulto)",
    it: "Verificato (adulto)", pt: "Verificado (adulto)", "pt-BR": "Verificado (adulto)",
    ru: "Подтверждён (взрослый)", ar: "تم التحقق (بالغ)", hi: "सत्यापित (वयस्क)", id: "Terverifikasi (dewasa)",
  },
  "settings.content.unverified": {
    ko: "미확인", "en-US": "Not verified", ja: "未確認", zh: "未确认", fr: "Non vérifié",
    de: "Nicht bestätigt", es: "Sin verificar", it: "Non verificato", pt: "Não verificado",
    "pt-BR": "Não verificado", ru: "Не подтверждён", ar: "غير مُتحقَّق", hi: "असत्यापित", id: "Belum terverifikasi",
  },
  "settings.content.showNsfw": {
    ko: "성인(NSFW) 목록 표시", "en-US": "Show adult (NSFW) listings", ja: "成人向け（NSFW）一覧を表示",
    zh: "显示成人（NSFW）内容", fr: "Afficher le contenu adulte (NSFW)", de: "Erwachsene (NSFW) Inhalte anzeigen",
    es: "Mostrar contenido adulto (NSFW)", it: "Mostra contenuti per adulti (NSFW)",
    pt: "Mostrar conteúdo adulto (NSFW)", "pt-BR": "Mostrar conteúdo adulto (NSFW)",
    ru: "Показывать материалы 18+ (NSFW)", ar: "إظهار المحتوى للبالغين (NSFW)",
    hi: "वयस्क (NSFW) सूची दिखाएँ", id: "Tampilkan daftar dewasa (NSFW)",
  },
  "settings.content.showNsfwHint": {
    ko: "연령 확인이 완료된 경우에만 허브에서 성인 콘텐츠가 노출됩니다.",
    "en-US": "Adult content appears in the hub only after age verification.",
    ja: "年齢確認が完了している場合にのみ、ハブで成人向けコンテンツが表示されます。",
    zh: "仅在完成年龄确认后，社区中才会显示成人内容。",
    fr: "Le contenu adulte n'apparaît dans le hub qu'après la vérification de l'âge.",
    de: "Erwachseneninhalte erscheinen im Hub erst nach der Altersbestätigung.",
    es: "El contenido adulto aparece en el hub solo tras la verificación de edad.",
    it: "I contenuti per adulti appaiono nell'hub solo dopo la verifica dell'età.",
    pt: "O conteúdo adulto aparece no hub apenas após a verificação de idade.",
    "pt-BR": "O conteúdo adulto aparece no hub apenas após a verificação de idade.",
    ru: "Материалы 18+ появляются в хабе только после подтверждения возраста.",
    ar: "يظهر محتوى البالغين في المركز فقط بعد التحقق من العمر.",
    hi: "वयस्क सामग्री हब में केवल आयु सत्यापन के बाद दिखती है।",
    id: "Konten dewasa muncul di hub hanya setelah verifikasi usia.",
  },
  "settings.content.ageConfirm": {
    ko: "성인(NSFW) 콘텐츠를 표시하려면 만 19세 이상이어야 합니다. 만 19세 이상이 맞습니까?",
    "en-US": "You must be of legal adult age to view adult (NSFW) content. Are you of legal adult age?",
    ja: "成人向け（NSFW）コンテンツを表示するには、法定成人年齢に達している必要があります。あなたは法定成人年齢に達していますか？",
    zh: "查看成人（NSFW）内容须达到法定成年年龄。您是否已达到法定成年年龄？",
    fr: "Vous devez avoir l'âge légal de la majorité pour voir le contenu adulte (NSFW). Avez-vous l'âge légal de la majorité ?",
    de: "Sie müssen volljährig sein, um Erwachseneninhalte (NSFW) zu sehen. Sind Sie volljährig?",
    es: "Debes tener la mayoría de edad legal para ver contenido adulto (NSFW). ¿Eres mayor de edad?",
    it: "Devi avere la maggiore età legale per vedere contenuti per adulti (NSFW). Hai la maggiore età?",
    pt: "Tens de ter a maioridade legal para ver conteúdo adulto (NSFW). Tens a maioridade legal?",
    "pt-BR": "Você precisa ter a maioridade legal para ver conteúdo adulto (NSFW). Você é maior de idade?",
    ru: "Для просмотра материалов 18+ (NSFW) вы должны быть совершеннолетним. Вы достигли совершеннолетия?",
    ar: "يجب أن تكون قد بلغت سن الرشد القانوني لعرض محتوى البالغين (NSFW). هل بلغت سن الرشد القانوني؟",
    hi: "वयस्क (NSFW) सामग्री देखने के लिए आपकी आयु कानूनी वयस्क आयु होनी चाहिए। क्या आप कानूनी रूप से वयस्क हैं?",
    id: "Anda harus berusia dewasa secara hukum untuk melihat konten dewasa (NSFW). Apakah Anda sudah dewasa secara hukum?",
  },
  "settings.content.ageReset": {
    ko: "연령 확인 해제", "en-US": "Reset age verification", ja: "年齢確認を解除",
    zh: "重置年龄确认", fr: "Réinitialiser la vérification de l'âge", de: "Altersbestätigung zurücksetzen",
    es: "Restablecer verificación de edad", it: "Reimposta verifica dell'età",
    pt: "Repor verificação de idade", "pt-BR": "Redefinir verificação de idade",
    ru: "Сбросить подтверждение возраста", ar: "إعادة ضبط التحقق من العمر",
    hi: "आयु सत्यापन रीसेट करें", id: "Atur ulang verifikasi usia",
  },
  "settings.content.reset": {
    ko: "초기화", "en-US": "Reset", ja: "リセット", zh: "重置", fr: "Réinitialiser",
    de: "Zurücksetzen", es: "Restablecer", it: "Reimposta", pt: "Repor", "pt-BR": "Redefinir",
    ru: "Сбросить", ar: "إعادة ضبط", hi: "रीसेट", id: "Atur ulang",
  },
  "settings.securityNotice": {
    ko: "API 키는 기기에 암호화되어 저장됩니다. 다만 웹앱 특성상 완벽한 보안은 어려우니, 공용 PC에서는 사용 후 키를 지우는 걸 권장합니다.",
    "en-US": "API keys are stored encrypted on your device. However, perfect security isn't possible in a web app, so we recommend deleting your keys after use on shared computers.",
    ja: "API キーは端末に暗号化して保存されます。ただしウェブアプリの性質上、完全なセキュリティは難しいため、共用 PC では使用後にキーを削除することをおすすめします。",
    zh: "API 密钥以加密方式存储在您的设备上。但网页应用无法做到绝对安全，因此建议在公用电脑上使用后删除密钥。",
    fr: "Les clés API sont stockées chiffrées sur votre appareil. Toutefois, une sécurité parfaite est impossible dans une application web ; nous recommandons donc de supprimer vos clés après usage sur un ordinateur partagé.",
    de: "API-Schlüssel werden verschlüsselt auf Ihrem Gerät gespeichert. Perfekte Sicherheit ist in einer Web-App jedoch nicht möglich – löschen Sie Ihre Schlüssel daher nach der Nutzung an gemeinsam genutzten Computern.",
    es: "Las claves API se guardan cifradas en tu dispositivo. Sin embargo, la seguridad perfecta no es posible en una app web, así que recomendamos borrar tus claves tras usarlas en ordenadores compartidos.",
    it: "Le chiavi API sono memorizzate cifrate sul tuo dispositivo. Tuttavia, in un'app web la sicurezza perfetta non è possibile: consigliamo di eliminare le chiavi dopo l'uso su computer condivisi.",
    pt: "As chaves de API são guardadas cifradas no teu dispositivo. Contudo, a segurança perfeita não é possível numa aplicação web, por isso recomendamos apagar as chaves após usá-las em computadores partilhados.",
    "pt-BR": "As chaves de API são armazenadas criptografadas no seu dispositivo. Porém, segurança perfeita não é possível em um app web, então recomendamos apagar suas chaves após o uso em computadores compartilhados.",
    ru: "Ключи API хранятся на устройстве в зашифрованном виде. Однако идеальная безопасность в веб-приложении невозможна, поэтому на общих компьютерах рекомендуем удалять ключи после использования.",
    ar: "تُخزَّن مفاتيح API مشفّرة على جهازك. لكن الأمان المثالي غير ممكن في تطبيق ويب، لذا ننصح بحذف مفاتيحك بعد الاستخدام على الأجهزة المشتركة.",
    hi: "API कुंजियाँ आपके डिवाइस पर एन्क्रिप्टेड रूप में संग्रहीत होती हैं। फिर भी वेब ऐप में पूर्ण सुरक्षा संभव नहीं है, इसलिए साझा कंप्यूटरों पर उपयोग के बाद कुंजियाँ हटाने की सलाह दी जाती है।",
    id: "Kunci API disimpan terenkripsi di perangkat Anda. Namun, keamanan sempurna tidak mungkin di aplikasi web, jadi sebaiknya hapus kunci Anda setelah digunakan di komputer bersama.",
  },
} satisfies Record<string, Row>;

/**
 * 전체 번역 테이블 = 기본(BASE) + 영역별 모듈 머지. 키 충돌은 없어야 하며(영역별
 * 접두사로 네임스페이스 분리), TKey 유니온은 이 머지 결과에서 자동 파생된다.
 */
const MESSAGES = {
  ...BASE_MESSAGES,
  ...settingsExtraMessages,
  ...chatMessages,
  ...characterMessages,
  ...gameMessages,
  ...gameEditorMessages,
  ...hubMessages,
  ...lorebookMessages,
  ...scriptMessages,
  ...miscMessages,
} satisfies Record<string, Row>;

/** 번역 가능한 모든 키. 머지된 MESSAGES에서 파생된다. */
export type TKey = keyof typeof MESSAGES;

/** key를 locale로 번역(누락 시 en-US → ko 폴백). params가 있으면 {name}을 치환한다. */
export function translate(locale: Locale, key: TKey, params?: Record<string, string | number>): string {
  const row = (MESSAGES as Record<string, Row>)[key];
  if (!row) return key;
  let s = row[locale] ?? row["en-US"] ?? row.ko ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return s;
}
